

import { Champignons, Espece, Nuisible } from '@repo/types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { services } from '../api/services';

export interface InventoryDataState {
  // Data
  champignons: Champignons[];
  especes: Espece[];
  nuisibles: Nuisible[];

  // Loading states
  isLoadingChampignons: boolean;
  isLoadingEspeces: boolean;
  isLoadingNuisibles: boolean;

  // Error states
  champignonsError: string | null;
  especesError: string | null;
  nuisiblesError: string | null;

  // Actions
  loadChampignons: () => Promise<void>;
  loadEspeces: () => Promise<void>;
  loadNuisibles: () => Promise<void>;
  loadAllData: () => Promise<void>;

  // Utility methods
  getChampignonById: (id: number) => Champignons | undefined;
  getEspeceById: (id: number) => Espece | undefined;
  getNuisibleById: (id: number) => Nuisible | undefined;

  // Clear methods
  clearChampignons: () => void;
  clearEspeces: () => void;
  clearNuisibles: () => void;
  clearAllData: () => void;
}

export const useInventoryDataStore = create<InventoryDataState>()(
  persist(
    (set, get) => ({
      // Initial state
      champignons: [],
      especes: [],
      nuisibles: [],

      isLoadingChampignons: false,
      isLoadingEspeces: false,
      isLoadingNuisibles: false,

      champignonsError: null,
      especesError: null,
      nuisiblesError: null,

      // Load champignons
      loadChampignons: async () => {
        set({ isLoadingChampignons: true, champignonsError: null });

        try {
          const response = await services.champignon.getChampignons({
            page: 1,
            limit: 1000, // Load all champignons
            includeDeleted: false
          });

          set({
            champignons: response.data,
            isLoadingChampignons: false
          });
        } catch (error) {
          set({
            champignonsError: error instanceof Error ? error.message : 'Failed to load champignons',
            isLoadingChampignons: false
          });
        }
      },

      // Load especes
      loadEspeces: async () => {
        set({ isLoadingEspeces: true, especesError: null });

        try {
          const response = await services.espece.getEspeces({
            page: 1,
            limit: 1000, // Load all especes
            includeDeleted: false
          });

          set({
            especes: response.data,
            isLoadingEspeces: false
          });
        } catch (error) {
          set({
            especesError: error instanceof Error ? error.message : 'Failed to load especes',
            isLoadingEspeces: false
          });
        }
      },

      // Load nuisibles
      loadNuisibles: async () => {
        set({ isLoadingNuisibles: true, nuisiblesError: null });

        try {
          const response = await services.nuisible.getNuisibles({
            page: 1,
            limit: 1000, // Load all nuisibles
            includeDeleted: false
          });

          set({
            nuisibles: response.data,
            isLoadingNuisibles: false
          });
        } catch (error) {
          set({
            nuisiblesError: error instanceof Error ? error.message : 'Failed to load nuisibles',
            isLoadingNuisibles: false
          });
        }
      },

      // Load all data
      loadAllData: async () => {
        const { loadChampignons, loadEspeces, loadNuisibles } = get();

        await Promise.allSettled([
          loadChampignons(),
          loadEspeces(),
          loadNuisibles()
        ]);
      },

      // Utility methods
      getChampignonById: (id: number) => {
        const { champignons } = get();
        return champignons.find(champignon => champignon.id === id);
      },

      getEspeceById: (id: number) => {
        const { especes } = get();
        return especes.find(espece => espece.id === id);
      },

      getNuisibleById: (id: number) => {
        const { nuisibles } = get();
        return nuisibles.find(nuisible => nuisible.id === id);
      },

      // Clear methods
      clearChampignons: () => {
        set({
          champignons: [],
          champignonsError: null,
          isLoadingChampignons: false
        });
      },

      clearEspeces: () => {
        set({
          especes: [],
          especesError: null,
          isLoadingEspeces: false
        });
      },

      clearNuisibles: () => {
        set({
          nuisibles: [],
          nuisiblesError: null,
          isLoadingNuisibles: false
        });
      },

      clearAllData: () => {
        set({
          champignons: [],
          especes: [],
          nuisibles: [],
          isLoadingChampignons: false,
          isLoadingEspeces: false,
          isLoadingNuisibles: false,
          champignonsError: null,
          especesError: null,
          nuisiblesError: null,
        });
      },
    }),
    {
      name: 'dendromap-inventory-data-storage',
      partialize: (state) => ({
        champignons: state.champignons,
        especes: state.especes,
        nuisibles: state.nuisibles,
      }),
    }
  )
);
